<template>
  <div class="container content-p">
    <component :is="profileData === undefined ? 'div' : 'b-card'">
      <!-- Alert: No item found -->
      <b-alert variant="danger" :show="profileData === undefined">
        <h4 class="alert-heading">Error fetching profile data</h4>
        <div class="alert-body">
          No profile found with this profile id. Check
          <b-link class="alert-link" :to="{ name: 'apps-roster-list-pending' }"> Profile List </b-link>
          for other profiles.
        </div>
      </b-alert>

      <component :is="'div'" v-if="profileData">
        <h3>
          {{ profileData.student ? profileData.student.fullName : 'unknown' }}
          <small>{{ profileData.student ? profileData.student.myIpId : 'unknown' }}</small>
        </h3>

        <hr />

        <b-tabs pills lazy>
          <!-- Tab: Attributes -->
          <b-tab>
            <template #title>
              <feather-icon icon="PaperclipIcon" size="16" class="mr-0 mr-sm-50" />
              <span class="d-none d-sm-inline">Attributes</span>
            </template>
            <profile-edit-tab-attributes class="mt-2 pt-75" />
          </b-tab>

          <!-- Tab: Section (only in ?section= mode) -->
          <b-tab v-if="isSectionMode">
            <template #title>
              <feather-icon icon="AwardIcon" size="16" class="mr-0 mr-sm-50" />
              <span class="d-none d-sm-inline">Section</span>
            </template>
            <profile-edit-tab-section class="mt-2 pt-75" />
          </b-tab>

          <!-- Tab: Compliance (hidden) -->
          <b-tab v-if="false">
            <template #title>
              <feather-icon icon="ShieldIcon" size="16" class="mr-0 mr-sm-50" />
              <span class="d-none d-sm-inline">Compliance</span>
            </template>
            <profile-edit-tab-compliance :profile-data.sync="profileData"></profile-edit-tab-compliance>
          </b-tab>

          <!-- Tab: History -->
          <b-tab>
            <template #title>
              <feather-icon icon="FileTextIcon" size="16" class="mr-0 mr-sm-50" />
              <span class="d-none d-sm-inline">History</span>
            </template>
            <profile-edit-tab-history :profile-data.sync="profileData"></profile-edit-tab-history>
          </b-tab>
        </b-tabs>

        <!-- Sticky save bar (single save for the whole profile) -->
        <b-row class="profile-edit-save-bar mt-2 w-100">
          <b-col cols="12" lg="12">
            <b-button
              variant="primary"
              class="float-right"
              :block="$store.getters['app/currentBreakPoint'] === 'xs'"
              @click="doSaveChanges"
            >
              Save Changes
            </b-button>
            <b-button
              variant="outline-secondary"
              class="mb-1 mb-sm-0 mr-0 mr-sm-1 float-right"
              :block="$store.getters['app/currentBreakPoint'] === 'xs'"
              @click="$router.back()"
            >
              <feather-icon icon="ArrowLeftIcon" />
              Return
            </b-button>
          </b-col>
        </b-row>
      </component>
    </component>
  </div>
</template>

<script>
import { BTab, BTabs, BCard, BAlert, BLink, BCol, BRow, BButton } from 'bootstrap-vue';
import { ref, onMounted } from '@vue/composition-api';
import router from '@/router';
import store from '@/store';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { isMitigatingStatus } from '../profileOptions';
import ProfileEditTabAttributes from './tabs/ProfileEditTabAttributes.vue';
import ProfileEditTabSection from './tabs/ProfileEditTabSection.vue';
import ProfileEditTabHistory from './tabs/ProfileEditTabHistory.vue';
import ProfileEditModal from './ProfileEditModal.vue';
import ProfileEditTabCompliance from './tabs/ProfileEditTabCompliance.vue';

export default {
  components: {
    BTab,
    BTabs,
    BCard,
    BAlert,
    BLink,
    BCol,
    BRow,
    BButton,
    ToastificationContent,
    ProfileEditModal,

    ProfileEditTabAttributes,
    ProfileEditTabSection,
    ProfileEditTabHistory,
    ProfileEditTabCompliance,
  },
  provide() {
    return { profileEdit: this };
  },
  data() {
    return {
      profileDataLocal: { preferred: {}, attributes: { retake: 'no' } },
      sectionId: undefined,
      propagation: { mode: 'current_future', effectiveAt: null },
      propagationOptions: [
        { label: 'None', value: 'none' },
        { label: 'Current', value: 'current' },
        { label: 'Current & Future', value: 'current_future' },
        { label: 'Future', value: 'future' },
        { label: 'All', value: 'all' },
      ],
    };
  },
  computed: {
    isSectionMode() {
      return Boolean(this.sectionId);
    },
    isCourseCohort() {
      return this.profileData && this.profileData.sections && Object.keys(this.profileData.sections).length === 1;
    },
  },
  watch: {
    profileData() {
      this.initLocal();
    },
    'profileDataLocal.attributes.retake'(val) {
      if (this.isCourseCohort) {
        this.profileDataLocal.sections[Object.keys(this.profileData.sections)[0]].retake = val;
      }
    },
  },
  mounted() {
    const { section } = this.$route.query;
    if (section) {
      this.sectionId = section;
    }
  },
  methods: {
    initLocal() {
      if (!this.profileData) {
        return;
      }

      this.profileDataLocal = { ...this.profileData };

      if (this.profileData.metadata) {
        this.profileDataLocal.enrollmentMilitaryAit = this.profileData.metadata.enrollmentMilitaryAit;
      }

      if (this.profileData.attributes && this.profileData.attributes.acceptanceAt) {
        this.profileDataLocal.acceptanceAt = this.profileData.attributes.acceptanceAt;
      }

      if (this.sectionId) {
        if (!this.profileDataLocal.sections || !this.profileDataLocal.sections[this.sectionId]) {
          this.profileDataLocal.sections = { ...(this.profileDataLocal.sections || {}) };
          this.$set(this.profileDataLocal.sections, this.sectionId, {
            examVoucher: 'no',
            examTaken: 'no',
            examPassed: 'no',
            labCompleted: 'no',
            status: 'pending',
          });
        }
      }
    },
    doSaveChanges() {
      const id = this.profileData.id;

      const pl = {
        attributes: this.profileDataLocal.attributes,
        status: this.profileDataLocal.status,
        statusReason: this.profileDataLocal.statusReason,
        statusDate: this.profileDataLocal.statusDate,
        mitigatingCircumstance: isMitigatingStatus(this.profileDataLocal.status)
          ? this.profileDataLocal.mitigatingCircumstance || null
          : null,
        graduationDate: this.profileDataLocal.graduationDate,
        preferred: this.profileDataLocal.preferred,
        representative: this.profileDataLocal.representative,
        sections: this.profileDataLocal.sections,
        reassignReason: this.profileDataLocal.reassignReason,
        reassignStart: this.profileDataLocal.reassignStart,
        reassignEnd: this.profileDataLocal.reassignEnd,
        reassignFrom: this.profileDataLocal.reassignFrom,

        options: { propagation: this.propagation },
      };

      if (this.profileDataLocal.representativeSelected) {
        pl.representative = this.profileDataLocal.representativeSelected.id;
      }

      if (this.profileDataLocal.acceptanceAt) {
        pl.attributes.acceptanceAt = this.profileDataLocal.acceptanceAt;
      }

      if (this.profileData.metadata && this.profileDataLocal.enrollmentMilitaryAit !== this.profileData.metadata.enrollmentMilitaryAit) {
        pl.metadata = {
          ...this.profileData.metadata,
          enrollmentMilitaryAit: this.profileDataLocal.enrollmentMilitaryAit,
        };
      }

      store
        .dispatch('rosterStoreModule/updateProfile', { id, profileData: pl })
        .then((response) => {
          this.profileDataLocal = response.data;

          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Profile Edited!',
              icon: 'EditIcon',
              variant: 'success',
            },
          });

          this.fetchProfile();
        })
        .catch((error) => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Failed to edit profile',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error.response ? error.response.data.message : error.message,
            },
          });
        });
    },
  },
  setup() {
    const profileData = ref(null);

    const fetchProfile = async () => {
      store
        .dispatch('rosterStoreModule/fetchProfile', {
          id: router.currentRoute.params.id,
          queryParams: {
            populate: 'representative student compliance history',
          },
        })
        .then((response) => {
          profileData.value = response.data;
        })
        .catch((error) => {
          if (error.response.status === 404) {
            profileData.value = undefined;
          }
        });
    };

    onMounted(() => fetchProfile());

    return {
      profileData,
      fetchProfile,
    };
  },
};
</script>

<style></style>
